"use client"

/**
 * ProductRecommendations Component
 *
 * A reusable component for displaying product recommendations in calculator results.
 * Fetches products from Directus based on specified categories and displays them
 * in a responsive grid layout with affiliate link tracking.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { ProductCard } from "@/components/ui/product-card"
import { InlineProductCard } from "@/components/ui/inline-product-card"
import { AffiliatePicksCard } from "@/components/tools/affiliate-picks-card"
import { Package, Loader2, ArrowRight } from "lucide-react"
import Link from "next/link"
import { useIsMobile } from "@/hooks/use-is-mobile"
import { usePathname } from "next/navigation"
import { trackEvent, contentAnalytics, calculatorTypeProps } from "@/lib/analytics"
import { resolveCalculatorType } from "@/lib/calculator-tool-types"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import { DirectusProduct } from "@/lib/directus"
import { withAmazonTag } from "@/lib/affiliate"

type ProductTrackingMetadata = Record<string, string | number | boolean | undefined>

const ASIN_PATTERNS = [
  /\/dp\/([A-Z0-9]{10})(?:[/?]|$)/i,
  /\/gp\/product\/([A-Z0-9]{10})(?:[/?]|$)/i,
  /[?&]asin=([A-Z0-9]{10})(?:&|$)/i,
]

function extractAsin(url: string | null | undefined): string | undefined {
  if (!url) return undefined
  for (const pattern of ASIN_PATTERNS) {
    const match = url.match(pattern)
    if (match) return match[1].toUpperCase()
  }
  return undefined
}

interface ProductRecommendationsProps {
  /** Product categories to fetch (e.g., "Fertilizers & Soil Health") */
  categories: string[]
  /** Maximum number of products to show */
  limit?: number
  /** Minimum products required to render at all. If fewer resolve, the block
   *  renders nothing rather than a partial (1-2 card) shelf. Default: no floor,
   *  so existing callers are unaffected. */
  minProducts?: number
  /** Title for the section */
  title?: string
  /** Semantic heading level for compact and picks-card titles. */
  headingLevel?: "h2" | "h3"
  /** Description text */
  description?: string
  /** Context for tracking (e.g., "fertilizer-calculator") */
  trackingContext?: string
  /** Optional custom icon component */
  icon?: React.ReactNode
  /** Compact mode - uses horizontal list layout on all screens */
  compact?: boolean
  /** Optional keywords for relevance scoring (e.g., grass type, season) */
  keywords?: string[]
  /** Page climate for structured season matching: 'warm' | 'cool' | 'transition'.
   *  Tagged products (Directus `season_affinity`) matching this are boosted. */
  season?: string
  /** Page lead grass species for structured matching, e.g. ['bermuda']. Matched
   *  against Directus `grass_types`; boosts the species' own products. */
  grassTypes?: string[]
  /** Render the "Affiliate CTA — 3 items" picks card (serif header + ranked
   *  rows + "Check price" CTAs) from the Claude Design system, instead of the
   *  compact inline list or the plain white grid card. */
  pickCard?: boolean
  /** Small uppercase kicker above the title in the picks card. Defaults to "Our picks". */
  eyebrow?: string
  /** When true, require keyword match even for in-category products. Render nothing if no match. */
  strict?: boolean
  /** Hide the "As an Amazon Associate..." footer. Use sparingly — the page
   *  must still surface the disclosure somewhere visible to comply with FTC
   *  endorsement guides. Intended for inline single-product mentions where
   *  another instance of ProductRecommendations on the same page already
   *  carries the disclosure. */
  hideAffiliateDisclosure?: boolean
  /** Extra analytics props merged into product click events. */
  trackingMetadata?: ProductTrackingMetadata
  /** Called once after products load, for surface-specific impression tracking. */
  onProductsShown?: (products: DirectusProduct[]) => void
  /** ASINs to filter out after fetch. Lets a secondary product block on the
   *  same page suppress items a primary block already surfaced (e.g. the
   *  soil-temp "Want your own reading?" thermometer when the phase block above
   *  already listed it), so the same product never renders twice. */
  excludeAsins?: string[]
  /** Server-selected picks for the first render, so the block is in the static
   *  HTML. Already limited and ASIN-filtered. The client still refetches on
   *  mount and swaps in the fresh list without a loading state. */
  initialProducts?: DirectusProduct[]
}

export function ProductRecommendations({
  categories,
  limit = 3,
  minProducts,
  title = "Recommended Products",
  headingLevel = "h3",
  description,
  trackingContext = "calculator",
  icon,
  compact = false,
  keywords,
  season,
  grassTypes,
  pickCard = false,
  eyebrow,
  strict = false,
  hideAffiliateDisclosure = false,
  trackingMetadata,
  onProductsShown,
  excludeAsins,
  initialProducts,
}: ProductRecommendationsProps) {
  const [products, setProducts] = useState<DirectusProduct[]>(initialProducts ?? [])
  const [isLoading, setIsLoading] = useState(!initialProducts)
  const [hasError, setHasError] = useState(false)
  const isMobile = useIsMobile()
  const shownProductsKeyRef = useRef<string | null>(null)
  // Same source `calculator_used` reads: the /tools route decides the
  // calculator. Off /tools (result page, blog embeds) the tracking context is
  // the only calculator identity there is, so it stands in.
  const pathname = usePathname()
  const calculatorType = resolveCalculatorType(pathname) ?? trackingContext
  // Every /tools route earns under the tool tracking ID; elsewhere the site tag.
  const isToolRoute = Boolean(pathname?.startsWith('/tools'))
  const taggedProducts = useMemo(
    () =>
      isToolRoute
        ? products.map((p) => (p.affiliate_url ? { ...p, affiliate_url: withAmazonTag(p.affiliate_url, 'tool') } : p))
        : products,
    [products, isToolRoute],
  )

  // Self-instrumented impression. `trackingContext` doubles as the affiliate
  // surface so `affiliate_product_shown` joins to `affiliate_product_clicked`
  // for CTR. Only attached on the compact/grid branches — the picks-card branch
  // fires its own impression + click inside AffiliatePicksCard, so attaching
  // here too would double-count.
  const impressionRef = useAffiliateImpression(trackingContext, {
    count: products.length,
    ...trackingMetadata,
  })

  // Legacy click event only. Used directly by the picks card, which already
  // fires the unified `affiliate_product_clicked` itself.
  const emitLegacyProductClick = useCallback((product: DirectusProduct) => {
    const position = products.findIndex((p) => p.id === product.id) + 1
    trackEvent('calculator_product_clicked', {
      product_id: product.id,
      product_name: product.name,
      product_category: product.category,
      asin: extractAsin(product.affiliate_url),
      tracking_context: trackingContext,
      ...calculatorTypeProps(calculatorType),
      ...(position > 0 ? { position } : {}),
      ...trackingMetadata,
    })
  }, [products, trackingContext, calculatorType, trackingMetadata])

  // Legacy + unified click, for the compact and grid branches.
  const handleProductClick = useCallback((product: DirectusProduct) => {
    emitLegacyProductClick(product)
    if (!trackingContext) return
    const position = products.findIndex((p) => p.id === product.id) + 1
    contentAnalytics.affiliateProductClicked(trackingContext, {
      productName: product.name,
      affiliateLink: product.affiliate_url ?? '',
      asin: extractAsin(product.affiliate_url) ?? null,
      ...(position > 0 ? { position } : {}),
      ...trackingMetadata,
    })
  }, [emitLegacyProductClick, products, trackingContext, trackingMetadata])

  const handleSeeAllClick = useCallback(() => {
    trackEvent('calculator_product_see_all_clicked', {
      product_category: categories[0],
      tracking_context: trackingContext,
      product_count: products.length,
    })
  }, [categories, trackingContext, products.length])

  const hasInitialProducts = initialProducts !== undefined
  useEffect(() => {
    let isMounted = true

    const loadProducts = async () => {
      try {
        if (!hasInitialProducts) setIsLoading(true)
        setHasError(false)

        // Fetch products from each category. When excludeAsins is set we
        // over-fetch by that count so the post-filter slice can still reach the
        // requested `limit` — otherwise excluding the page's own product would
        // silently shrink a 3-item block to 2.
        const allProducts: DirectusProduct[] = []
        const seenIds = new Set<string>()
        const fetchLimit = limit + (excludeAsins?.length ?? 0)

        for (const category of categories) {
          if (allProducts.length >= fetchLimit) break

          try {
            const tagParam = keywords && keywords.length > 0 ? `&tags=${encodeURIComponent(keywords.join(','))}` : ''
            const strictParam = strict ? '&strict=true' : ''
            const seasonParam = season ? `&season=${encodeURIComponent(season)}` : ''
            const grassParam = grassTypes && grassTypes.length > 0 ? `&grassTypes=${encodeURIComponent(grassTypes.join(','))}` : ''
            const response = await fetch(
              `/api/blog/product-recommendations?category=${encodeURIComponent(category)}&limit=${fetchLimit}${tagParam}${strictParam}${seasonParam}${grassParam}`,
              {
                headers: {
                  'Cache-Control': 'no-cache',
                  'Pragma': 'no-cache'
                }
              }
            )

            if (!response.ok) continue

            const data = await response.json()
            const categoryProducts = data.products || []

            // Add products we haven't seen yet
            for (const product of categoryProducts) {
              if (allProducts.length >= fetchLimit) break
              if (!seenIds.has(product.id)) {
                seenIds.add(product.id)
                allProducts.push(product)
              }
            }
          } catch (error) {
            console.error(`Error loading products for category ${category}:`, error)
            continue
          }
        }

        // Suppress any ASINs a sibling block already surfaced on this page.
        const excludeSet = new Set((excludeAsins ?? []).map((a) => a.toUpperCase()))
        const visibleProducts = excludeSet.size
          ? allProducts.filter((p) => {
              const asin = extractAsin(p.affiliate_url)
              return !asin || !excludeSet.has(asin)
            })
          : allProducts

        if (isMounted && !(hasInitialProducts && visibleProducts.length === 0)) {
          setProducts(visibleProducts.slice(0, limit))
        }
      } catch (error) {
        console.error('Error loading product recommendations:', error)
        if (isMounted && !hasInitialProducts) {
          setHasError(true)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    // Small delay to avoid blocking initial render
    const timer = setTimeout(loadProducts, 100)
    return () => {
      clearTimeout(timer)
      isMounted = false
    }
  }, [categories, limit, keywords, strict, season, grassTypes, excludeAsins, hasInitialProducts])

  useEffect(() => {
    if (!onProductsShown || products.length === 0) return
    const productsKey = products.map((product) => product.id).join('|')
    if (shownProductsKeyRef.current === productsKey) return
    shownProductsKeyRef.current = productsKey
    onProductsShown(products)
  }, [onProductsShown, products])

  // Don't render if loading
  if (isLoading) {
    return (
      <Card className="border-gray-200 shadow-lg">
        <CardContent className="p-6 md:p-8">
          <div className="flex items-center justify-center min-h-[150px]">
            <div className="text-center">
              <Loader2 className="w-6 h-6 text-green-600 animate-spin mx-auto mb-3" />
              <p className="text-gray-600 text-sm">Loading product recommendations...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Don't render if error, no products, or fewer than a required floor (callers
  // that need a full shelf pass minProducts to avoid a partial 1-2 card block).
  if (hasError || products.length === 0 || (minProducts !== undefined && products.length < minProducts)) {
    return null
  }

  // Use list view on mobile or when compact mode is enabled
  const useListView = isMobile || compact
  const Heading = headingLevel

  if (compact) {
    return (
      <section
        ref={impressionRef}
        aria-labelledby="inline-product-rec-heading"
        className="rounded-2xl border border-gray-200 bg-white p-4 md:p-5 animate-in fade-in duration-300 motion-reduce:animate-none"
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <Heading
            id="inline-product-rec-heading"
            className="inline-flex items-center gap-2 text-sm font-bold tracking-tight text-gray-900"
          >
            {icon || <Package className="h-4 w-4 text-green-600" />}
            {title}
          </Heading>
          <Link
            href={`/products?category=${encodeURIComponent(categories[0])}${trackingContext ? `&source=${encodeURIComponent(trackingContext)}` : ''}`}
            onClick={handleSeeAllClick}
            className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:text-green-800 transition-colors"
          >
            See all
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <ul className="list-none p-0 m-0 space-y-2.5" aria-label="Recommended products">
          {taggedProducts.map((product, index) => (
            <li key={`${product.id}-${index}`} className="list-none">
              <InlineProductCard
                product={product}
                rank={index + 1}
                showRank={product.best_seller || index === 0}
                placement={trackingContext}
                onClick={handleProductClick}
              />
            </li>
          ))}
        </ul>
        {!hideAffiliateDisclosure && (
          <p className="mt-3 text-xs text-gray-400 text-center">
            As an Amazon Associate, we earn from qualifying purchases.
          </p>
        )}
      </section>
    )
  }

  // "Affiliate CTA — 3 items" picks card (Claude Design). This is the standard
  // for any full-width, 3-product block: auto-selected whenever a non-compact
  // block resolves to exactly three products (or forced via `pickCard`). Compact
  // sidebar/inline placements keep their dense list; other counts keep the grid.
  const usePicksCard = pickCard || (!compact && products.length === 3)
  if (usePicksCard) {
    const seeAllHref = `/products?category=${encodeURIComponent(categories[0])}${trackingContext ? `&source=${encodeURIComponent(trackingContext)}` : ''}`
    return (
      <div className="animate-in fade-in duration-300 motion-reduce:animate-none">
        <AffiliatePicksCard
          title={title}
          headingLevel={headingLevel}
          eyebrow={eyebrow}
          description={description}
          products={taggedProducts}
          onProductClick={emitLegacyProductClick}
          hideDisclosure={hideAffiliateDisclosure}
          seeAllHref={seeAllHref}
          onSeeAll={handleSeeAllClick}
          surface={trackingContext}
        />
      </div>
    )
  }

  return (
    <Card ref={impressionRef} className="border-gray-200 shadow-lg animate-in fade-in duration-300 motion-reduce:animate-none">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg flex items-center gap-2">
          {icon || <Package className="w-5 h-5 text-green-600" />}
          {title}
        </CardTitle>
        {description && (
          <CardDescription>{description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        <ul
          className={`list-none p-0 m-0 ${useListView ? "space-y-3 sm:space-y-4" : "grid md:grid-cols-2 lg:grid-cols-3 gap-4"}`}
          aria-label="Recommended products"
        >
          {taggedProducts.map((product, index) => (
            <li key={`${product.id}-${index}`} className="list-none">
              <ProductCard
                product={product}
                index={index}
                variant={product.featured ? "featured" : product.best_seller ? "bestSeller" : "default"}
                viewMode={useListView ? "list" : "grid"}
                placement={trackingContext}
                onProductClick={handleProductClick}
              />
            </li>
          ))}
        </ul>
        <div className="mt-4 pt-3 border-t border-gray-100 text-center">
          <Link
            href={`/products?category=${encodeURIComponent(categories[0])}${trackingContext ? `&source=${encodeURIComponent(trackingContext)}` : ''}`}
            onClick={handleSeeAllClick}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600 hover:text-green-700 transition-colors"
          >
            See all {categories[0]} products
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}
